/** @file PadletOOXMLShape schema
 * OOXML shape is a shape that is defined in the Open Office XML format, used in Google Slides and PowerPoint.
 * This shape is a wrapper around the Tldraw shape, and is used to render OOXML shapes in Tldraw.
 * Docs: https://ecma-international.org/wp-content/uploads/ECMA-376-1_5th_edition_december_2016.zip
 */
import {
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  defaultColorNames,
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultFontStyle,
  DefaultHorizontalAlignStyle,
  DefaultSizeStyle,
  DefaultVerticalAlignStyle,
  richTextValidator,
  StyleProp,
  T,
  TLBaseShape,
  TLDefaultColorStyle,
  TLDefaultDashStyle,
  TLDefaultFillStyle,
  TLDefaultFontStyle,
  TLDefaultHorizontalAlignStyle,
  TLDefaultSizeStyle,
  TLDefaultVerticalAlignStyle,
  TLRichText,
} from 'tldraw'

export const DefaultLabelColorStyle = StyleProp.defineEnum('tldraw:padletOOXMLShapeLabelColor', {
  defaultValue: 'black',
  values: defaultColorNames,
})

// OOXML shape properties - matching geo shape structure
const padletOOXMLShapeProps = {
  // Core shape dimensions
  w: T.nonZeroNumber,
  h: T.nonZeroNumber,

  // Visual styling
  color: DefaultColorStyle,
  fill: DefaultFillStyle,
  dash: DefaultDashStyle,
  size: DefaultSizeStyle,

  // Text properties
  font: DefaultFontStyle,
  align: DefaultHorizontalAlignStyle,
  verticalAlign: DefaultVerticalAlignStyle,
  labelColor: DefaultLabelColorStyle,
  richText: richTextValidator,
  growY: T.positiveNumber,
  scale: T.nonZeroNumber,

  // SVG path for OOXML shape rendering
  svgPath: T.string,
}

// TypeScript interface for OOXML shape properties
interface PadletOOXMLShapeProps {
  // Core properties (same as geo)
  w: number
  h: number
  color: TLDefaultColorStyle
  fill: TLDefaultFillStyle
  dash: TLDefaultDashStyle
  size: TLDefaultSizeStyle
  font: TLDefaultFontStyle
  align: TLDefaultHorizontalAlignStyle
  verticalAlign: TLDefaultVerticalAlignStyle
  labelColor: TLDefaultColorStyle
  richText: TLRichText
  growY: number
  scale: number
  svgPath: string
}

// OOXML shape type definition
type PadletOOXMLShape = TLBaseShape<'padlet-ooxml-shape', PadletOOXMLShapeProps>

const padletOOXMLShapeVersions = createShapePropsMigrationIds('padlet-ooxml-shape', {
  AddSvgPath: 1,
})

const padletOOXMLShapeMigrations = createShapePropsMigrationSequence({
  sequence: [
    {
      id: padletOOXMLShapeVersions.AddSvgPath,
      up: (props) => {
        if (!('svgPath' in props)) {
          // Set default empty string for svgPath
          props.svgPath = ''
        }
      },
      down: (props) => {
        // No-op, explicitly no down state so that we force clients to update.
        // delete props.svgPath
      },
    },
  ],
})

export {
  PadletOOXMLShape,
  padletOOXMLShapeMigrations,
  PadletOOXMLShapeProps,
  padletOOXMLShapeProps,
  padletOOXMLShapeVersions,
}
